import { useState, useCallback, useEffect, useRef } from "react";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import { searchPlugin } from "@react-pdf-viewer/search";
import { motion } from "framer-motion";
import {
  X,
  Download,
  Highlighter,
  StickyNote,
  Trash2,
  Save,
  Cloud,
  CloudOff,
  Loader2,
  Pencil,
  Eraser,
  FileText,
} from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import axios from "axios";

// Import styles
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import "@react-pdf-viewer/search/lib/styles/index.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Drawing Canvas Component
const DrawingCanvas = ({ width, height, drawings, setDrawings, currentPage, isDrawingMode, strokeColor, strokeWidth }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });

  // Redraw all drawings when they change
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw all strokes for current page
    const pageDrawings = drawings.filter(d => d.page === currentPage);
    pageDrawings.forEach(drawing => {
      ctx.beginPath();
      ctx.strokeStyle = drawing.color;
      ctx.lineWidth = drawing.width;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      
      if (drawing.points.length > 0) {
        ctx.moveTo(drawing.points[0].x, drawing.points[0].y);
        drawing.points.forEach(point => {
          ctx.lineTo(point.x, point.y);
        });
        ctx.stroke();
      }
    });
  }, [drawings, currentPage]);

  const getMousePos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    if (!isDrawingMode) return;
    setIsDrawing(true);
    const pos = getMousePos(e);
    setLastPos(pos);
    
    // Start new stroke
    setDrawings(prev => [...prev, {
      id: Date.now().toString(),
      page: currentPage,
      color: strokeColor,
      width: strokeWidth,
      points: [pos],
      timestamp: new Date().toISOString()
    }]);
  };

  const draw = (e) => {
    if (!isDrawing || !isDrawingMode) return;
    
    const pos = getMousePos(e);
    
    // Add point to current stroke
    setDrawings(prev => {
      const updated = [...prev];
      const lastStroke = updated[updated.length - 1];
      if (lastStroke && lastStroke.page === currentPage) {
        lastStroke.points.push(pos);
      }
      return updated;
    });
    
    setLastPos(pos);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className={`absolute inset-0 z-10 ${isDrawingMode ? 'cursor-crosshair' : 'pointer-events-none'}`}
      style={{ width: '100%', height: '100%' }}
      onMouseDown={startDrawing}
      onMouseMove={draw}
      onMouseUp={stopDrawing}
      onMouseLeave={stopDrawing}
    />
  );
};

export const PDFViewer = ({
  pdfUrl,
  fileName,
  materialId,
  onClose,
  onDownload,
}) => {
  const [highlights, setHighlights] = useState([]);
  const [notes, setNotes] = useState([]);
  const [drawings, setDrawings] = useState([]);
  const [showNotes, setShowNotes] = useState(true);
  const [newNote, setNewNote] = useState("");
  const [isHighlightMode, setIsHighlightMode] = useState(false);
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [selectedText, setSelectedText] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [cloudSynced, setCloudSynced] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [strokeColor, setStrokeColor] = useState("#ff0000");
  const [strokeWidth, setStrokeWidth] = useState(3);
  const [useIframeFallback, setUseIframeFallback] = useState(false);
  const pdfContainerRef = useRef(null);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("mms_token")}` },
  });

  // Initialize search plugin
  const searchPluginInstance = searchPlugin();

  // Initialize default layout plugin with search
  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    sidebarTabs: (defaultTabs) => [defaultTabs[0], defaultTabs[1]],
    toolbarPlugin: {
      searchPlugin: {
        keyword: "",
      },
    },
  });

  // Load PDF - try signed URL first, fallback to blob
  useEffect(() => {
    const loadPdf = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);
        setUseIframeFallback(false);
        
        // First, check if we can get a signed URL from the materials URL endpoint
        try {
          const urlResponse = await axios.get(
            `${API}/materials/url/${materialId}`,
            getAuthHeaders()
          );
          
          if (urlResponse.data.signed_url) {
            // Use signed URL directly - works for GCS stored files
            setPdfBlobUrl(urlResponse.data.signed_url);
            setIsLoading(false);
            return;
          }
        } catch (urlError) {
          console.log("URL endpoint error, falling back to blob download");
        }
        
        // For local files or as fallback: Download as blob
        const response = await axios.get(pdfUrl, {
          ...getAuthHeaders(),
          responseType: "blob",
        });

        const blob = new Blob([response.data], { type: "application/pdf" });
        const blobUrl = URL.createObjectURL(blob);
        setPdfBlobUrl(blobUrl);
      } catch (error) {
        console.error("Failed to load PDF:", error);
        setLoadError("Failed to load PDF file.");
      } finally {
        setIsLoading(false);
      }
    };

    if (materialId) {
      loadPdf();
    }

    return () => {
      // Only revoke if it's a blob URL (starts with blob:)
      if (pdfBlobUrl && pdfBlobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [pdfUrl, materialId]);

  // Load annotations from cloud on mount
  useEffect(() => {
    const loadAnnotations = async () => {
      try {
        const response = await axios.get(
          `${API}/annotations/${materialId}`,
          getAuthHeaders()
        );

        if (response.data) {
          setHighlights(response.data.highlights || []);
          setNotes(response.data.notes || []);
          setDrawings(response.data.drawings || []);
          setCloudSynced(true);
        }
      } catch (error) {
        console.error("Failed to load annotations:", error);
        const savedHighlights = localStorage.getItem(
          `mms_highlights_${materialId}`
        );
        const savedNotes = localStorage.getItem(`mms_notes_${materialId}`);
        const savedDrawings = localStorage.getItem(`mms_drawings_${materialId}`);
        if (savedHighlights) setHighlights(JSON.parse(savedHighlights));
        if (savedNotes) setNotes(JSON.parse(savedNotes));
        if (savedDrawings) setDrawings(JSON.parse(savedDrawings));
        setCloudSynced(false);
      }
    };

    loadAnnotations();
  }, [materialId]);

  // Save to cloud
  const saveToCloud = useCallback(async () => {
    setIsSaving(true);
    try {
      await axios.post(
        `${API}/annotations/${materialId}`,
        { highlights, notes, drawings },
        getAuthHeaders()
      );

      localStorage.setItem(
        `mms_highlights_${materialId}`,
        JSON.stringify(highlights)
      );
      localStorage.setItem(`mms_notes_${materialId}`, JSON.stringify(notes));
      localStorage.setItem(`mms_drawings_${materialId}`, JSON.stringify(drawings));

      setCloudSynced(true);
      toast.success("Saved to cloud!");
    } catch (error) {
      console.error("Failed to save to cloud:", error);
      localStorage.setItem(
        `mms_highlights_${materialId}`,
        JSON.stringify(highlights)
      );
      localStorage.setItem(`mms_notes_${materialId}`, JSON.stringify(notes));
      localStorage.setItem(`mms_drawings_${materialId}`, JSON.stringify(drawings));
      setCloudSynced(false);
      toast.error("Cloud sync failed - saved locally");
    } finally {
      setIsSaving(false);
    }
  }, [materialId, highlights, notes, drawings]);

  // Handle text selection for highlighting
  const handleTextSelection = useCallback(() => {
    if (!isHighlightMode || isDrawingMode) return;

    const selection = window.getSelection();
    const text = selection?.toString().trim();

    if (text && text.length > 0) {
      setSelectedText(text);
    }
  }, [isHighlightMode, isDrawingMode]);

  const addHighlight = () => {
    if (selectedText) {
      const newHighlight = {
        id: Date.now().toString(),
        text: selectedText,
        page: currentPage,
        timestamp: new Date().toISOString(),
      };
      setHighlights((prev) => [...prev, newHighlight]);
      setSelectedText("");
      window.getSelection()?.removeAllRanges();
      toast.success("Text highlighted!");
    }
  };

  const removeHighlight = (id) => {
    setHighlights((prev) => prev.filter((h) => h.id !== id));
  };

  const addNote = () => {
    if (newNote.trim()) {
      const note = {
        id: Date.now().toString(),
        text: newNote,
        page: currentPage,
        timestamp: new Date().toISOString(),
      };
      setNotes((prev) => [...prev, note]);
      setNewNote("");
      toast.success("Note added!");
    }
  };

  const removeNote = (id) => {
    setNotes((prev) => prev.filter((n) => n.id !== id));
  };

  const clearDrawingsOnPage = () => {
    setDrawings(prev => prev.filter(d => d.page !== currentPage));
    toast.success("Drawings cleared on this page");
  };

  const handlePageChange = (e) => {
    setCurrentPage(e.currentPage + 1);
  };

  const currentPageHighlights = highlights.filter((h) => h.page === currentPage);
  const currentPageNotes = notes.filter((n) => n.page === currentPage);
  const currentPageDrawings = drawings.filter((d) => d.page === currentPage);

  const colorOptions = [
    { color: "#ff0000", name: "Red" },
    { color: "#0066ff", name: "Blue" },
    { color: "#00cc00", name: "Green" },
    { color: "#ff9900", name: "Orange" },
    { color: "#9900ff", name: "Purple" },
    { color: "#000000", name: "Black" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex bg-black/95">
      {/* Main PDF Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="flex items-center justify-between px-6 py-4 bg-zinc-900 border-b border-white/10">
          <div className="flex items-center gap-4">
            <button
              data-testid="pdf-close-btn"
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
            <h2 className="text-lg font-semibold text-white truncate max-w-md">
              {fileName}
            </h2>
            <div className="flex items-center gap-2">
              {cloudSynced ? (
                <Cloud className="w-4 h-4 text-green-400" />
              ) : (
                <CloudOff className="w-4 h-4 text-yellow-400" />
              )}
              <span className="text-xs text-slate-400">
                {cloudSynced ? "Synced" : "Local only"}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Drawing Mode Toggle */}
            <Button
              data-testid="pdf-drawing-toggle"
              onClick={() => {
                setIsDrawingMode(!isDrawingMode);
                if (!isDrawingMode) setIsHighlightMode(false);
              }}
              className={`p-2 rounded-lg transition-colors ${
                isDrawingMode
                  ? "bg-blue-500/30"
                  : "bg-white/10 hover:bg-white/20"
              }`}
            >
              <Pencil className="w-5 h-5 text-white" />
            </Button>

            {/* Highlight Mode Toggle */}
            <Button
              data-testid="pdf-highlight-toggle"
              onClick={() => {
                setIsHighlightMode(!isHighlightMode);
                if (!isHighlightMode) setIsDrawingMode(false);
              }}
              className={`p-2 rounded-lg transition-colors ${
                isHighlightMode
                  ? "bg-yellow-500/30"
                  : "bg-white/10 hover:bg-white/20"
              }`}
            >
              <Highlighter className="w-5 h-5 text-white" />
            </Button>

            {/* Notes Toggle */}
            <Button
              data-testid="pdf-notes-toggle"
              onClick={() => setShowNotes(!showNotes)}
              className={`p-2 rounded-lg transition-colors ${
                showNotes ? "bg-white/20" : "bg-white/10 hover:bg-white/20"
              }`}
            >
              <StickyNote className="w-5 h-5 text-white" />
            </Button>

            {/* Save Button */}
            <Button
              data-testid="pdf-save-btn"
              onClick={saveToCloud}
              disabled={isSaving}
              className="p-2 rounded-lg bg-green-500/20 hover:bg-green-500/30 transition-colors disabled:opacity-50"
            >
              {isSaving ? (
                <Loader2 className="w-5 h-5 text-green-400 animate-spin" />
              ) : (
                <Save className="w-5 h-5 text-green-400" />
              )}
            </Button>

            {/* Download */}
            <Button
              data-testid="pdf-download-btn"
              onClick={onDownload}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            >
              <Download className="w-5 h-5 text-white" />
            </Button>
          </div>
        </header>

        {/* Drawing Mode Banner */}
        {isDrawingMode && (
          <div className="px-6 py-2 bg-blue-500/20 border-b border-blue-500/30 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <span className="text-sm text-blue-300">
                Drawing mode active
              </span>
              <div className="flex items-center gap-1">
                {colorOptions.map(({ color, name }) => (
                  <button
                    key={color}
                    onClick={() => setStrokeColor(color)}
                    className={`w-6 h-6 rounded-full border-2 transition-all ${
                      strokeColor === color ? "border-white scale-110" : "border-transparent"
                    }`}
                    style={{ backgroundColor: color }}
                    title={name}
                  />
                ))}
              </div>
              <select
                value={strokeWidth}
                onChange={(e) => setStrokeWidth(Number(e.target.value))}
                className="bg-white/10 text-white text-sm rounded px-2 py-1"
              >
                <option value={2}>Thin</option>
                <option value={3}>Medium</option>
                <option value={5}>Thick</option>
                <option value={8}>Extra Thick</option>
              </select>
            </div>
            <Button
              onClick={clearDrawingsOnPage}
              className="text-sm bg-red-500/20 hover:bg-red-500/30 text-red-300 px-3 py-1 rounded-lg flex items-center gap-1"
            >
              <Eraser className="w-4 h-4" />
              Clear Page
            </Button>
          </div>
        )}

        {/* Highlight Mode Banner */}
        {isHighlightMode && (
          <div className="px-6 py-2 bg-yellow-500/20 border-b border-yellow-500/30 flex items-center justify-between">
            <span className="text-sm text-yellow-300">
              Highlight mode active - Select text to highlight
            </span>
            {selectedText && (
              <Button
                data-testid="add-highlight-btn"
                onClick={addHighlight}
                className="text-sm bg-yellow-500 text-black px-4 py-1 rounded-lg hover:bg-yellow-400"
              >
                Highlight "{selectedText.substring(0, 30)}
                {selectedText.length > 30 ? "..." : ""}"
              </Button>
            )}
          </div>
        )}

        {/* PDF Document */}
        <div
          ref={pdfContainerRef}
          className="flex-1 overflow-hidden bg-zinc-950 relative"
          onMouseUp={handleTextSelection}
        >
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="w-10 h-10 border-2 border-white/20 border-t-white rounded-full animate-spin mb-4" />
              <p className="text-slate-400">Loading PDF...</p>
            </div>
          ) : loadError ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-8">
              <X className="w-16 h-16 text-red-400 mb-4" />
              <p className="text-red-400 text-lg font-medium">Failed to load PDF</p>
              <p className="text-slate-500 mt-2 max-w-md">
                {loadError}
              </p>
              <Button
                onClick={onDownload}
                className="mt-4 bg-white/10 hover:bg-white/20 text-white px-6 py-2 rounded-lg"
              >
                <Download className="w-4 h-4 mr-2" />
                Download File
              </Button>
            </div>
          ) : pdfBlobUrl && useIframeFallback ? (
            // Iframe fallback for maximum compatibility
            <div className="h-full w-full flex flex-col">
              <div className="bg-zinc-800 px-4 py-2 flex items-center justify-between border-b border-white/10">
                <span className="text-sm text-slate-300 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Native PDF Viewer
                </span>
                <Button
                  onClick={onDownload}
                  className="text-xs bg-white/10 hover:bg-white/20 text-white px-3 py-1 rounded"
                >
                  <Download className="w-3 h-3 mr-1" />
                  Download
                </Button>
              </div>
              <iframe
                src={pdfBlobUrl}
                title={fileName}
                className="flex-1 w-full bg-white"
                style={{ border: 'none' }}
              />
            </div>
          ) : pdfBlobUrl ? (
            <Worker workerUrl="/pdf.worker.min.js">
              <div
                style={{ height: "100%", width: "100%" }}
                className="rpv-core__viewer"
              >
                {isDrawingMode && (
                  <DrawingCanvas
                    width={800}
                    height={1100}
                    drawings={drawings}
                    setDrawings={setDrawings}
                    currentPage={currentPage}
                    isDrawingMode={isDrawingMode}
                    strokeColor={strokeColor}
                    strokeWidth={strokeWidth}
                  />
                )}
                <Viewer
                  fileUrl={pdfBlobUrl}
                  plugins={[defaultLayoutPluginInstance, searchPluginInstance]}
                  onPageChange={handlePageChange}
                  defaultScale={1}
                  theme="dark"
                  renderError={(error) => {
                    // Switch to iframe fallback on render error
                    setTimeout(() => setUseIframeFallback(true), 100);
                    return (
                      <div className="flex flex-col items-center justify-center h-full">
                        <div className="w-10 h-10 border-2 border-white/20 border-t-white rounded-full animate-spin mb-4" />
                        <p className="text-slate-400">Switching to native viewer...</p>
                      </div>
                    );
                  }}
                />
              </div>
            </Worker>
          ) : null}
        </div>
      </div>

      {/* Notes Sidebar */}
      {showNotes && (
        <motion.aside
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: 350, opacity: 1 }}
          exit={{ width: 0, opacity: 0 }}
          className="w-[350px] bg-zinc-900 border-l border-white/10 flex flex-col"
        >
          <div className="p-4 border-b border-white/10">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">
                Annotations
              </h3>
              {cloudSynced ? (
                <Cloud className="w-4 h-4 text-green-400" />
              ) : (
                <CloudOff className="w-4 h-4 text-yellow-400" />
              )}
            </div>
            <p className="text-xs text-slate-400 mt-1">Page {currentPage}</p>
          </div>

          {/* Add Note */}
          <div className="p-4 border-b border-white/10">
            <textarea
              data-testid="note-input"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Add a note for this page..."
              className="w-full h-24 bg-white/5 border border-white/10 rounded-xl p-3 text-white text-sm placeholder:text-slate-500 resize-none focus:outline-none focus:border-white/20"
            />
            <Button
              data-testid="add-note-btn"
              onClick={addNote}
              disabled={!newNote.trim()}
              className="mt-2 w-full bg-white text-black py-2 rounded-lg hover:bg-white/90 disabled:opacity-50 transition-colors"
            >
              Add Note
            </Button>
          </div>

          {/* Notes & Highlights List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Current Page Drawings */}
            {currentPageDrawings.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-blue-400 mb-2 flex items-center gap-2">
                  <Pencil className="w-4 h-4" />
                  Drawings on this page ({currentPageDrawings.length})
                </h4>
              </div>
            )}

            {/* Current Page Highlights */}
            {currentPageHighlights.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-yellow-400 mb-2 flex items-center gap-2">
                  <Highlighter className="w-4 h-4" />
                  Highlights on this page
                </h4>
                <div className="space-y-2">
                  {currentPageHighlights.map((highlight) => (
                    <div
                      key={highlight.id}
                      className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 group"
                    >
                      <p className="text-sm text-yellow-200 line-clamp-3">
                        "{highlight.text}"
                      </p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-slate-500">
                          {new Date(highlight.timestamp).toLocaleDateString()}
                        </span>
                        <button
                          onClick={() => removeHighlight(highlight.id)}
                          className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-500/20 rounded transition-all"
                        >
                          <Trash2 className="w-3 h-3 text-red-400" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Current Page Notes */}
            {currentPageNotes.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-blue-400 mb-2 flex items-center gap-2">
                  <StickyNote className="w-4 h-4" />
                  Notes on this page
                </h4>
                <div className="space-y-2">
                  {currentPageNotes.map((note) => (
                    <div
                      key={note.id}
                      className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 group"
                    >
                      <p className="text-sm text-blue-200">{note.text}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-slate-500">
                          {new Date(note.timestamp).toLocaleDateString()}
                        </span>
                        <button
                          onClick={() => removeNote(note.id)}
                          className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-500/20 rounded transition-all"
                        >
                          <Trash2 className="w-3 h-3 text-red-400" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* All Highlights Summary */}
            {highlights.length > 0 &&
              highlights.some((h) => h.page !== currentPage) && (
                <div className="pt-4 border-t border-white/10">
                  <h4 className="text-sm font-medium text-slate-300 mb-2">
                    All Highlights ({highlights.length})
                  </h4>
                  <div className="space-y-1">
                    {highlights.map((h) => (
                      <button
                        key={h.id}
                        onClick={() => setCurrentPage(h.page)}
                        className={`w-full text-left p-2 rounded-lg text-xs transition-colors ${
                          h.page === currentPage
                            ? "bg-yellow-500/20 text-yellow-300"
                            : "bg-white/5 text-slate-400 hover:bg-white/10"
                        }`}
                      >
                        <span className="font-medium">Page {h.page}:</span>{" "}
                        {h.text.substring(0, 40)}...
                      </button>
                    ))}
                  </div>
                </div>
              )}

            {/* All Notes Summary */}
            {notes.length > 0 && notes.some((n) => n.page !== currentPage) && (
              <div className="pt-4 border-t border-white/10">
                <h4 className="text-sm font-medium text-slate-300 mb-2">
                  All Notes ({notes.length})
                </h4>
                <div className="space-y-1">
                  {notes.map((n) => (
                    <button
                      key={n.id}
                      onClick={() => setCurrentPage(n.page)}
                      className={`w-full text-left p-2 rounded-lg text-xs transition-colors ${
                        n.page === currentPage
                          ? "bg-blue-500/20 text-blue-300"
                          : "bg-white/5 text-slate-400 hover:bg-white/10"
                      }`}
                    >
                      <span className="font-medium">Page {n.page}:</span>{" "}
                      {n.text.substring(0, 40)}...
                    </button>
                  ))}
                </div>
              </div>
            )}

            {highlights.length === 0 && notes.length === 0 && drawings.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                <StickyNote className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No annotations yet</p>
                <p className="text-xs mt-1">
                  Use drawing, highlight, or notes to annotate
                </p>
              </div>
            )}
          </div>
        </motion.aside>
      )}
    </div>
  );
};
